<template>
  <div
    v-loading="isLoading"
    element-loading-background="rgba(241, 241, 248, 0.3)"
    class="tw-relative tw-h-full tw-overflow-hidden tw-overscroll-y-contain"
  >
    <div
      ref="MessagingMessages"
      class="MessagingMessages tw-overflow-auto tw-flex tw-flex-col tw-w-full tw-max-w-3xl tw-mx-auto"
      :class="[
        isFullscreen ? 'is-fullscreen' : '',
        showSendMessage ? 'has-send-message' : '',
        areFiltersExpanded ? 'are-filters-expanded' : '',
      ]"
      @scroll="handleScroll"
    >
      <i-button
        v-if="moreMessagesUrl"
        v-loading="isMoreMessagesLoading"
        class="tw-my-3 tw-mx-6"
        @click="loadMoreMessages"
      >
        {{ $t('messaging.load_previous') }}
      </i-button>
      <div
        v-if="messages.length === 0"
        class="tw-flex tw-h-full tw-justify-center tw-items-center"
      >
        {{ $t('messaging.no_messages') }}
      </div>
      <template
        v-for="message in messages"
        :key="message.id"
      >
        <div
          v-if="message.isFirstUnseen || message.isFirstToday"
          class="MessageBreaker"
        >
          <span v-if="message.isFirstUnseen">
            {{ $t('messaging.new_message_breaker') }}
          </span>
          <span v-else>
            {{ $t('messaging.today_message_breaker') }}
          </span>
        </div>
        <ForeignersMessagingMessage
          :ref="`Message-${message.id}`"
          :message="message"
          :show-permit="showPermit"
          :logged-in-from-foreigner-account="true"
        />
      </template>
    </div>
    <div
      v-if="showSendMessage"
      ref="MessengerFooter"
      class="messenger-footer tw-w-full tw-bg-inherit tw-flex tw-gap-3 tw-px-6 tw-py-3 tw-max-w-3xl tw-mx-auto"
    >
      <el-popover
        :visible="isMentioningOpened"
        placement="bottom-start"
        :width="200"
        popper-class="tw-max-h-44 tw-max-w-52 tw-p-0"
      >
        <template #reference>
          <div>
            <el-input
              ref="messageInput"
              v-model="currentThreadNotSentMessage"
              type="textarea"
              :rows="1"
              :autosize="{ minRows: 1, maxRows: getFullScreenMessageInputMaxRows() }"
              :placeholder="$t('messaging.message')"
              class="messenger-input tw-w-full"
              @input="handleMessageInput"
              @keydown.ctrl.enter.native.prevent="sendMessage"
              @keydown.meta.enter.native.prevent="sendMessage"
              @keydown.shift.enter.native.prevent="sendMessage"
            />
          </div>
        </template>

        <div
          v-for="userGroups in filteredMentions"
          :key="userGroups.role_name"
          class="tw-py-2"
        >
          <div
            v-if="userGroups.users.length !== 0"
            class="user-group__name tw-text-center tw-text-gray-dark"
          >
            {{ userGroups.role_name }}
          </div>
          <template
            v-for="(user, index) in userGroups.users"
            :key="user.id"
          >
            <div
              v-if="index !== 0"
              class="tw-w-full tw-h-px tw-bg-light"
            />
            <div
              class="tw-px-4 tw-py-2 tw-cursor-pointer tw-transition-colors hover:tw-bg-light"
              @click="handleMentionClick(user)"
            >
              {{ user.fullname }}
            </div>
          </template>
        </div>
      </el-popover>
      <i-button
        type="primary"
        size="medium"
        :disabled="!currentThreadNotSentMessage.trim().length || isSendInProgress"
        is-icon
        @click="sendMessage"
      >
        <icon-message-send />
      </i-button>
    </div>
  </div>
</template>

<script>
import _debounce from 'lodash/debounce';
import ForeignersMessagingMessage from '@/components/Foreigners/Messaging/ForeignersMessagingMessage.vue';
import { getValidationError } from '@/helpers';
import { mapGetters, mapState } from 'vuex';
import { includesIgnoringDiacritics } from '@/helpers/searchWithDiacritics';

export default {
  components: {
    ForeignersMessagingMessage,
  },
  props: {
    isFullscreen: {
      type: Boolean,
      default: false,
    },
    areFiltersExpanded: {
      type: Boolean,
      default: false,
    },
    showPermit: {
      type: Boolean,
      default: false,
    },
    showSendMessage: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      isSendInProgress: false,
      isMoreMessagesLoading: false,
      unseenInterval: null,
      isMentioningOpened: false,
      filteredMentions: [],
      mentionsAutocomplete: '',
      cursorPosition: null,
      currentFooterHeight: 64,
      expandedFiltersPartHeight: 100,
    };
  },
  computed: {
    ...mapState({
      me: (state) => state.v2.shared.profile.me,
      foreigner: (state) => state.v2.foreigner.messaging.foreigner,
      moreMessagesUrl: (state) => state.v2.foreigner.messaging.moreMessagesUrl,
      fulltextMessageId: (state) => state.v2.foreigner.messaging.fulltextMessageId,
      openedThreadData: (state) => state.v2.foreigner.messaging.openedThreadData,
      messagingUsers: (state) => {
        return state.v2.foreigner.messaging.messagingUsers.map((userGroup) => {
          if (userGroup.role_name === 'clients') {
            return {
              ...userGroup,
              users: [
                {
                  fullname: 'All HRs',
                  id: -2,
                  roleLetter: userGroup.role_name[0],
                },
                ...userGroup.users,
              ],
            };
          }
          return userGroup;
        });
      },
    }),
    ...mapGetters({
      messages: 'v2/foreigner/messaging/getMessages',
      unseenMessagesIds: 'v2/foreigner/messaging/getUnseenMessagesIds',
    }),
    messengerContainerHeight() {
      return this.isFullscreen ? '100vh' : '60vh';
    },
    notSentMessageThreadId() {
      return this.openedThreadData.id;
    },
    notSentMessageForeignerId() {
      return this.openedThreadData.foreignerId;
    },
    currentThreadNotSentMessage: {
      get() {
        if (this.openedThreadData.id === 'all') return '';
        return this.$store.state.v2.foreigner.messaging.notSentMessages?.[this.notSentMessageForeignerId]?.[this.notSentMessageThreadId] ?? '';
      },
      set(value) {
        this.$store.commit('v2/foreigner/messaging/updateNotSentMessages',{ message: value, threadId: this.notSentMessageThreadId, foreignerId: this.notSentMessageForeignerId });
      },
    },
  },

  watch: {
    openedThreadData: {
      immediate: true,
      async handler() {
        await this.loadMessages();
      },
    },
    '$store.state.v2.foreigner.messaging.filters': {
      deep: true,
      handler() {
        this.loadMessages();
      },
    },
    fulltextMessageId: {
      async handler() {
        this.loadMessages();
      },
    },
    areFiltersExpanded() {
      this.updateMessagesContainerHeight(true);
    },
    currentThreadNotSentMessage: {
      immediate: true,
      handler(value) {
        if (value.length === 0) {
          this.isMentioningOpened = false;
          return;
        }

        if (this.isMentioningOpened) {
          // get all text after last @ in input
          const text = value.split('@').pop();

          // filter this.messagingUsers.map(userGroup => ({...userGroup, users: userGroup.users.filter(user => user.fullname.toLowerCase().includes(text.toLowerCase()))}))
          this.filteredMentions = this.messagingUsers.map((userGroup) => ({
            ...userGroup,
            users: userGroup.users.filter((user) => includesIgnoringDiacritics(user.fullname, text)),
          }));
        }

        if (value[value.length - 1] === '@') {
          this.filteredMentions = this.messagingUsers;
          this.isMentioningOpened = true;
        }
      },
    },
  },

  beforeUnmount() {
    clearInterval(this.unseenInterval);
  },

  methods: {
    loadMessages: _debounce(async function() {
      if (this.foreigner?.id == null) {
        return;
      }
      this.isLoading = true;
      const messageId = this.$route.params.mid;

      await this.$store.dispatch('v2/foreigner/messaging/loadMessages', {
        foreignerId: this.foreigner?.id,
        permitId: this.openedThreadData.id,
        messageId,
      });
      await this.$nextTick();

      if (this.fulltextMessageId) {
        this.scrollToFulltextMessage();
      } else if (messageId && this.$refs[`Message-${messageId}`]) {
        this.scrollToMessage(messageId);
      } else {
        this.scrollToBottom();
      }

      this.isLoading = false;

      clearInterval(this.unseenInterval);
      this.patchSeenMessages();
      this.unseenInterval = setInterval(() => {
        this.patchSeenMessages();
      }, 15 * 1000);
    }, 200),

    async loadMoreMessages() {
      this.isMoreMessagesLoading = true;
      const previousScrollPosition = this.$refs.MessagingMessages.scrollHeight;

      await this.$store.dispatch('v2/foreigner/messaging/loadMoreMessages');

      await this.$nextTick();
      this.$refs.MessagingMessages.scrollTop = this.$refs.MessagingMessages.scrollHeight - previousScrollPosition;
      this.isMoreMessagesLoading = false;
    },

    getFullScreenMessageInputMaxRows() {
      if (this.isFullscreen) {
        const maxMessageInputHeight = 800;
        const inputRowHeight = 32;
        const filtersAndMessagesMinHeight = 64 + 80;
        return Math.floor((Math.min(window.innerHeight - filtersAndMessagesMinHeight, maxMessageInputHeight)) / inputRowHeight);
      }
      return 6;
    },

    async patchSeenMessages() {
      if (this.unseenMessagesIds && this.unseenMessagesIds.length) {
        await this.$store.dispatch('v2/foreigner/messaging/patchSeenMessages', {
          foreignerId: this.foreigner.id,
          unseenMessagesIds: this.unseenMessagesIds,
        });
      }
    },

    scrollToBottom() {
      if (this.$refs.MessagingMessages) {
        this.$refs.MessagingMessages.scrollTop = this.$refs.MessagingMessages.scrollHeight;
      }
    },
    scrollToFulltextMessage() {
      if (this.$refs.MessagingMessages) {
        const message = this.$refs[`Message-${this.fulltextMessageId}`][0];
        if (message) {
          this.$refs.MessagingMessages.scrollTop = message.$el.offsetTop;
        }
      }
    },
    scrollToMessage(id) {
      if (this.$refs.MessagingMessages) {
        const message = this.$refs[`Message-${id}`][0];
        if (message) {
          this.$refs.MessagingMessages.scrollTop = message.$el.offsetTop;
        }
      }
    },
    async sendMessage() {
      if (this.isSendInProgress || !this.currentThreadNotSentMessage.trim().length) {
        return;
      }
      this.isSendInProgress = true;
      const foreignerId = this.foreigner?.id ?? this.foreignerId;

      // Extract all mentions in the format `@[${user.fullname}]` from `this.currentThreadNotSentMessage`
      const mentions = this.currentThreadNotSentMessage.match(/@\[([^]+?)]/g) || [];

      // For each mention, find the user object and replace the mention in `this.currentThreadNotSentMessage` with a corresponding placeholder
      let messageBody = this.currentThreadNotSentMessage;
      const mentionedUsers = mentions.map((mention) => {
        const mentionedUserFullname = mention.slice(2, -1); // remove @[] from the mention, leaving the fullname
        const user = this.messagingUsersFlatted.find((user) => user.fullname === mentionedUserFullname);
        if (user) {
          messageBody = messageBody.replace(mention, user.fullname); // replace mention with placeholder in message body
        }
        return user;
      }).filter(Boolean); // filter out undefined entries

      // Extract the ids from `mentionedUsers`
      const mentionedUsersIds = mentionedUsers.map((user) => user.id);

      // Combine foreignerId and mentionedUsers into a single array for recipients
      const recipients = Array.from(new Set([foreignerId, ...mentionedUsersIds]));

      try {
        await this.$store.dispatch('v2/foreigner/messaging/sendMessage', {
          fid: this.me.id,
          text: messageBody,
          permit_id: this.openedThreadData.id ? Number(this.openedThreadData.id) : undefined,
          recipients: recipients,
        });
        this.$store.commit('v2/foreigner/messaging/updateNotSentMessages', { message: '', threadId: this.notSentMessageThreadId, foreignerId: this.notSentMessageForeignerId });
        this.handleMessageInput();

        setTimeout(() => {
          this.updateMessagesContainerHeight(false);
          this.scrollToBottom();
        });
      } catch(error) {
        const errorText = getValidationError(error);
        this.$message.error({
          message: this.$capitalize(errorText || 'Error'),
          showClose: true,
          dangerouslyUseHTMLString: true,
        });
      } finally {
        this.isSendInProgress = false;
      }
    },

    handleScroll() {
      if (this.moreMessagesUrl && !this.isMoreMessagesLoading && this.$refs.MessagingMessages.scrollTop < 10) {
        this.loadMoreMessages();
      }
    },

    handleMessageInput() {
      this.updateCursorPosition();
      setTimeout(() => {
        this.updateMessagesContainerHeight(false);
      });
    },

    updateMessagesContainerHeight(isFiltersChanged) {
      const heightAdjustment = this.isFullscreen ? -63 : 0;
      const filtersAdjustment = this.areFiltersExpanded ? this.expandedFiltersPartHeight : 0;
      this.$refs.MessagingMessages.style.height = `calc(${this.messengerContainerHeight} - ${filtersAdjustment}px + ${heightAdjustment}px - ${this.$refs.MessengerFooter?.clientHeight ?? 0}px)`;

      const pxToScrollToStayAtSameMessagePosition = (this.$refs.MessengerFooter?.clientHeight ?? 0) - this.currentFooterHeight;
      this.currentFooterHeight = this.$refs.MessengerFooter?.clientHeight ?? 0;

      const maxScrollTop = this.$refs.MessagingMessages.scrollHeight - this.$refs.MessagingMessages.clientHeight;
      const isMaxScrolled = this.$refs.MessagingMessages.scrollTop === maxScrollTop;

      if (isFiltersChanged || this.areFiltersExpanded) {
        this.$refs.MessagingMessages.scrollTop += this.areFiltersExpanded ? this.expandedFiltersPartHeight : (!isMaxScrolled ? -this.expandedFiltersPartHeight : 0);
      } else if (!isMaxScrolled) {
        this.$refs.MessagingMessages.scrollTop += pxToScrollToStayAtSameMessagePosition;
      }
    },

    updateCursorPosition() {
      this.cursorPosition = this.$refs.messageInput.$el.selectionStart;
    },

    handleMentionClick(user) {
      const symbolPos = this.currentThreadNotSentMessage.lastIndexOf('@', this.cursorPosition);

      if (symbolPos !== -1) {
        const endOfNamePos = this.currentThreadNotSentMessage.indexOf(' ', symbolPos);
        const portionToReplace = this.currentThreadNotSentMessage
          .substring(symbolPos + 1, endOfNamePos !== -1 ? endOfNamePos : undefined)
          .toLowerCase();

        if (includesIgnoringDiacritics(user.fullname, portionToReplace)) {
          const replacement = `@[${user.fullname}]`;
          const updatedMessage = this.currentThreadNotSentMessage.slice(0, symbolPos) +
            replacement +
            (endOfNamePos !== -1 ? this.currentThreadNotSentMessage.slice(endOfNamePos) : '');
          this.$store.commit('v2/foreigner/messaging/updateNotSentMessages', { message: updatedMessage, threadId: this.notSentMessageThreadId, foreignerId: this.notSentMessageForeignerId });
        }

        this.$nextTick(() => this.$refs.messageInput.focus());
      }
    },
  },
};
</script>


<style lang="scss" scoped>
.MessagingMessages {
  height: 60vh;
  overscroll-behavior: none;
  @apply tw-pb-6;

  &.has-send-message {
    height: calc(60vh - 64px);
  }

  &.has-send-message.are-filters-expanded {
    height: calc(60vh - 16px - 148px);
  }

  &.is-fullscreen {
    height: calc(100vh - 63px);
  }

  &.is-fullscreen.has-send-message {
    height: calc(100vh - 63px - 64px);
  }

  .MessageBreaker {
    @apply tw-relative tw-mt-3 tw-mb-1 tw-w-11/12 tw-mx-auto;

    &::before,
    &::after {
      @apply tw-bg-gray-dark tw-absolute tw-h-px tw-block tw-top-0;
      @apply dark:tw-bg-dm-gray-dark;
      width: 45%;
      content: '';
    }

    &::after {
      @apply tw-right-0;
    }

    span {
      @apply tw-text-gray-dark tw-top-0 tw-text-xs tw-text-center tw-w-full tw-block tw--mt-2 tw-pt-px;
      @apply dark:tw-text-dm-gray-dark;
    }
  }
}

.messenger-footer {
  .el-tooltip__trigger {
    @apply tw-w-full;
  }
}

.user-group__name::first-letter {
  @apply tw-uppercase;
}
</style>
